<template>
  <v-form ref="form" lazy-validation>
    <v-skeleton-loader :loading="companyLoading" type="card-avatar, article, actions">
      <v-card flat>
        <v-card-title> Company Details </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="tempCompany.name"
                label="Company Name"
                :rules="defaultRules"
                required
                autocomplete="off"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="tempCompany.url"
                label="URL"
                autocomplete="off"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-autocomplete
                v-model="tempCompany.industry_group"
                label="Industry Group"
                :items="industryGroupTypes"
                clearable
                autocomplete="off"
              />
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-model="tempCompany.industry"
                :disabled="!tempCompany.industry_group"
                :items="companyIndustryTypes"
                label="Industry"
                clearable
                autocomplete="off"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="tempCompany.metrics.employees"
                label="Employees"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="tempCompany.metrics.annualRevenue"
                label="Annual Revenue"
                autocomplete="off"
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="tempCompany.metrics.estimatedAnnualRevenue"
                label="Estimated Annual Revenue"
                autocomplete="off"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="tempCompany.year_established"
                label="Year Established"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-textarea
                key="CompanyEditFormTextarea"
                v-model="tempCompany.description"
                label="Description"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-title>Company Contact Information</v-card-title>
        <CommonAddressWidget v-model="tempCompany.address" show-suite source="company" />
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <TimeZoneSelect v-model="tempCompany.address.timezone_name" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <CommonPhonePicker
                show-extension
                :phone-number="tempCompany.phone.office"
                :phone-extension="tempCompany.phone.office_ext"
                :phone-country-code="tempCompany.phone.office_country_code"
                @save:number="tempCompany.phone.office = $event"
                @save:extension="tempCompany.phone.office_ext = $event"
                @save:countryCode="tempCompany.phone.office_country_code = $event"
              >
                <template #default="{ on, attrs }">
                  <v-text-field
                    v-model="tempCompany.phone.office"
                    v-facade="getCountryMask(tempCompany.phone.office_country_code)"
                    :suffix="tempCompany.phone.office_ext ? `ext: ${tempCompany.phone.office_ext}` : null"
                    label="Office Phone"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
              </CommonPhonePicker>
            </v-col>
            <v-col cols="6">
              <CommonPhonePicker
                show-extension
                :phone-number="tempCompany.phone.fax"
                :phone-extension="tempCompany.phone.fax_ext"
                :phone-country-code="tempCompany.phone.fax_country_code"
                @save:number="tempCompany.phone.fax = $event"
                @save:extension="tempCompany.phone.fax_ext = $event"
                @save:countryCode="tempCompany.phone.fax_country_code = $event"
              >
                <template #default="{ on, attrs }">
                  <v-text-field
                    v-model="tempCompany.phone.fax"
                    v-facade="getCountryMask(tempCompany.phone.fax_country_code)"
                    :suffix="tempCompany.phone.fax_ext ? `ext: ${tempCompany.phone.fax_ext}` : null"
                    label="Office Fax"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
              </CommonPhonePicker>
            </v-col>
          </v-row>
        </v-card-text>
        <CompanyBrokerageSection
          v-model="tempCompany.brokerLicense"
          :is-brokerage.sync="tempCompany.isBrokerage"
        />
        <v-card-actions class="sc-navigation-drawer-actions">
          <v-btn
            v-if="tempCompany.active"
            class="archive"
            text
            :small="$vuetify.breakpoint.smAndDown"
            :disabled="loading || archiveLoading"
            :loading="archiveLoading"
            data-track="company.record.archive"
            @click="archiveHandler"
          >
            Archive
          </v-btn>
          <v-spacer />
          <v-btn
            class="cancel"
            text
            :small="$vuetify.breakpoint.smAndDown"
            @click="cancelCompany"
          >
            Cancel
          </v-btn>
          <v-btn
            :small="$vuetify.breakpoint.smAndDown"
            color="primary"
            class="px-4"
            :loading="loading"
            :disabled="archiveLoading"
            data-track="company.record.save"
            @click="saveCompany"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-skeleton-loader>
    <ArchiveConfirmationDialog
      v-model="archiveDialog"
      :loading="archiveLoading"
      label="company"
      @cancel="archiveDialog = false"
      @submit="confirmArchive"
    />
  </v-form>
</template>

<script>
import { detailedDiff } from 'deep-object-diff';
import { mapState } from 'vuex';

import { ServiceFactory } from '@/services/ServiceFactory';
import { getCompanyDefaultObj } from '@/bundles/Companies/helpers/companyDefaultData';
import { AppFormMixin } from '@/bundles/App/mixins/forms/AppFormMixin';
import { CountriesMasksMixin } from '@/bundles/Common/mixins/CountriesMasksMixin';

import CompanyBrokerageSection from '@/bundles/Companies/components/forms/CompanyBrokerageSection.vue';
import CommonPhonePicker from '@/bundles/Common/components/CommonPhonePicker.vue';
import TimeZoneSelect from '@/bundles/App/components/inputs/TimeZoneSelect.vue';
import CommonAddressWidget from '@/components/common-components/address/CommonAddressWidget.vue';
import ArchiveConfirmationDialog from '@/bundles/Common/components/ArchiveConfirmationDialog.vue';

const CompanyService = ServiceFactory.get('company');

export default {
  name: 'CompanyEditForm',

  components: {
    CommonAddressWidget,
    TimeZoneSelect,
    CommonPhonePicker,
    CompanyBrokerageSection,
    ArchiveConfirmationDialog,
  },

  mixins: [AppFormMixin, CountriesMasksMixin],

  props: {
    company: {
      type: Object,
      default: () => {},
    },
    sourceId: {
      type: String,
      default: '',
    },
    archiveLoading: Boolean,
  },

  data: () => ({
    valid: false,
    defaultRules: [(v) => !!v || 'Item is required'],
    isCompanyBrokerage: false,
    dbCompany: {},
    tempCompany: getCompanyDefaultObj(),
    companyLoading: false,
    loading: false,
    archiveDialog: false,
  }),

  computed: {
    ...mapState({
      token: (state) => state.idToken,
      industryGroupTypes: state => state.Company.industryGroupTypes,
      industryTypes: state => state.Company.industryTypes
    }),
    companyIndustryTypes () {
      const type = this.industryTypes.find(
        (item) => item.group === this.tempCompany.industry_group
      );
      return type ? type.types : [];
    },
  },

  mounted () {
    this.setCompany();
  },

  methods: {
    setCompany () {
      if (this.sourceId) {
        this.fetchCompany();
      } else {
        const stringData = JSON.stringify(this.company);
        this.dbCompany = JSON.parse(stringData);
        this.tempCompany = JSON.parse(stringData);
      }
    },
    fetchCompany: async function () {
      try {
        this.companyLoading = true;
        const { token } = this;
        const { data } = await CompanyService.get({ token, id: this.sourceId });
        const stringData = JSON.stringify(data);
        this.dbCompany = JSON.parse(stringData);
        this.tempCompany = JSON.parse(stringData);
        if (!this.tempCompany.metrics)
          this.tempCompany = Object.assign({}, this.tempCompany, {
            metrics: {
              employees: '',
              annualRevenue: '',
              estimatedAnnualRevenue: '',
            },
          });
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.companyLoading = false;
      }
    },
    async saveCompany () {
      try {
        if (!this.$refs.form.validate()) {
          this.showFormValidationErrorNotification();
          return;
        }

        this.loading = true;

        let changes = detailedDiff(this.dbCompany, this.tempCompany);
        let payload = Object.assign({}, changes.added, changes.updated, {
          date_update: Date.now(),
        });

        const updated = await CompanyService.update({
          token: this.token,
          payload,
          id: this.tempCompany._key,
        });

        this.addSuccessNotification('Company updated successfully');
        this.$emit('updated', updated.data);
      } catch (error) {
        this.addNotification({ ...error });
      } finally {
        this.loading = false;
      }
    },
    cancelCompany () {
      this.$emit('cancel');
    },
    archiveHandler () {
      this.archiveDialog = true;
    },
    confirmArchive () {
      this.$emit('archiveCompany', this.tempCompany._key);
      this.archiveDialog = false;
    }
  },
};
</script>

<style scoped>
.company-logo {
  max-width: 200px;
}
</style>
